import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react'
import styles from './stickyTableScrollbar.module.css'

export interface StickyTableScrollbarProps {
  /**
   * A wrapper that is (or contains) the horizontally scrollable element.
   * The real scroller is resolved automatically, so this can point at an
   * outer wrapper even when a nested child is the one that overflows.
   */
  containerRef: RefObject<HTMLElement | null>
  'data-testid'?: string
}

interface ScrollbarGeometry {
  visible: boolean
  left: number
  width: number
  scrollWidth: number
  clientWidth: number
  scrollLeft: number
}

const HIDDEN_GEOMETRY: ScrollbarGeometry = {
  visible: false,
  left: 0,
  width: 0,
  scrollWidth: 0,
  clientWidth: 0,
  scrollLeft: 0,
}

const OVERFLOW_TOLERANCE_PX = 1
const MIN_THUMB_WIDTH_PX = 40

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

/** Finds the element within `root` (or `root` itself) that scrolls horizontally. */
function resolveScroller(root: HTMLElement): HTMLElement | null {
  if (root.scrollWidth - root.clientWidth > OVERFLOW_TOLERANCE_PX) return root
  const descendants = root.querySelectorAll<HTMLElement>('*')
  for (const element of descendants) {
    if (element.scrollWidth - element.clientWidth > OVERFLOW_TOLERANCE_PX) {
      const overflowX = getComputedStyle(element).overflowX
      if (overflowX === 'auto' || overflowX === 'scroll') return element
    }
  }
  return null
}

function computeThumb(geometry: ScrollbarGeometry) {
  const maxScrollLeft = Math.max(0, geometry.scrollWidth - geometry.clientWidth)
  const ratio = geometry.scrollWidth > 0 ? geometry.clientWidth / geometry.scrollWidth : 1
  const thumbWidth = clamp(Math.round(ratio * geometry.width), MIN_THUMB_WIDTH_PX, geometry.width)
  const maxThumbLeft = Math.max(0, geometry.width - thumbWidth)
  const thumbLeft = maxScrollLeft > 0 ? (geometry.scrollLeft / maxScrollLeft) * maxThumbLeft : 0
  return { thumbWidth, thumbLeft, maxThumbLeft, maxScrollLeft }
}

/**
 * Renders an always-visible horizontal scrollbar fixed to the bottom of the
 * viewport that stays in sync with a table's horizontal scroll. Because the
 * thumb is drawn by us (not a native overlay scrollbar) it is visible at rest,
 * not only while scrolling. It shows while the table overflows horizontally and
 * its own scrollbar is still below the fold, so the user can scroll the columns
 * without first scrolling the page down to the table's bottom.
 */
export function StickyTableScrollbar({
  containerRef,
  'data-testid': dataTestId,
}: StickyTableScrollbarProps) {
  const scrollerRef = useRef<HTMLElement | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const geometryRef = useRef<ScrollbarGeometry>(HIDDEN_GEOMETRY)
  const [geometry, setGeometry] = useState<ScrollbarGeometry>(HIDDEN_GEOMETRY)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let frameId = 0
    let cachedScroller: HTMLElement | null = null

    const getScroller = () => {
      if (cachedScroller && cachedScroller.isConnected) return cachedScroller
      cachedScroller = resolveScroller(container)
      return cachedScroller
    }

    const update = () => {
      frameId = 0
      const scroller = getScroller()
      scrollerRef.current = scroller

      if (!scroller) {
        geometryRef.current = HIDDEN_GEOMETRY
        setGeometry((previous) => (previous.visible ? HIDDEN_GEOMETRY : previous))
        return
      }

      const rect = scroller.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const hasOverflow = scroller.scrollWidth - scroller.clientWidth > OVERFLOW_TOLERANCE_PX
      // The table's own scrollbar sits at rect.bottom; show the fixed bar only
      // while that bottom edge is below the viewport and the table is on screen.
      const nativeScrollbarBelowFold = rect.bottom > viewportHeight
      const tableInView = rect.top < viewportHeight

      const next: ScrollbarGeometry = {
        visible: hasOverflow && nativeScrollbarBelowFold && tableInView,
        left: rect.left,
        width: rect.width,
        scrollWidth: scroller.scrollWidth,
        clientWidth: scroller.clientWidth,
        scrollLeft: scroller.scrollLeft,
      }
      geometryRef.current = next

      setGeometry((previous) => {
        if (
          previous.visible === next.visible &&
          previous.left === next.left &&
          previous.width === next.width &&
          previous.scrollWidth === next.scrollWidth &&
          previous.clientWidth === next.clientWidth &&
          previous.scrollLeft === next.scrollLeft
        ) {
          return previous
        }
        return next
      })
    }

    const scheduleUpdate = () => {
      if (frameId) return
      frameId = requestAnimationFrame(update)
    }

    const invalidateAndUpdate = () => {
      cachedScroller = null
      scheduleUpdate()
    }

    update()

    // Capture phase so we react to scrolling of the window and any nested scroller.
    window.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true })
    window.addEventListener('resize', invalidateAndUpdate)

    const resizeObserver = new ResizeObserver(invalidateAndUpdate)
    resizeObserver.observe(container)

    // Column toggles / paging change the table width and possibly the scroller.
    const mutationObserver = new MutationObserver(invalidateAndUpdate)
    mutationObserver.observe(container, { childList: true, subtree: true })

    return () => {
      if (frameId) cancelAnimationFrame(frameId)
      window.removeEventListener('scroll', scheduleUpdate, { capture: true } as EventListenerOptions)
      window.removeEventListener('resize', invalidateAndUpdate)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [containerRef])

  const handleThumbPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const scroller = scrollerRef.current
    if (!scroller) return

    const startX = event.clientX
    const startScrollLeft = scroller.scrollLeft

    const onPointerMove = (moveEvent: globalThis.PointerEvent) => {
      const { maxThumbLeft, maxScrollLeft } = computeThumb(geometryRef.current)
      if (maxThumbLeft <= 0) return
      const deltaX = moveEvent.clientX - startX
      scroller.scrollLeft = startScrollLeft + (deltaX / maxThumbLeft) * maxScrollLeft
    }
    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
  }, [])

  const handleTrackPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current
    const track = trackRef.current
    if (!scroller || !track) return

    const { thumbWidth, maxThumbLeft, maxScrollLeft } = computeThumb(geometryRef.current)
    if (maxThumbLeft <= 0) return

    const trackRect = track.getBoundingClientRect()
    const targetThumbLeft = clamp(event.clientX - trackRect.left - thumbWidth / 2, 0, maxThumbLeft)
    scroller.scrollLeft = (targetThumbLeft / maxThumbLeft) * maxScrollLeft
  }, [])

  const { thumbWidth, thumbLeft } = computeThumb(geometry)

  return (
    <div
      ref={trackRef}
      className={styles.track}
      style={{
        left: geometry.left,
        width: geometry.width,
        display: geometry.visible ? 'block' : 'none',
      }}
      onPointerDown={handleTrackPointerDown}
      data-testid={dataTestId}
      aria-hidden
    >
      <div
        className={styles.thumb}
        style={{ width: thumbWidth, transform: `translateX(${thumbLeft}px)` }}
        onPointerDown={handleThumbPointerDown}
      />
    </div>
  )
}
